import { useEffect, useState } from "react";
import { fireAuditEvent } from "./auditEvent";
import { isAdminPosition, isManagerPlus, readUserPosition } from "../../access/positions";

// Global "demo mode" flag — controls whether scan pages render their demo
// barcode buttons. Stored in localStorage so the toggle persists across
// reloads, and so it can be flipped per-device (handy: ops devices stay
// off, QA workstations stay on).
//
// Pages should:  const demo = useDemoVisible();  then `{demo && <Demo .../>}`.
// useDemoVisible() folds in the per-device audience setting and the
// logged-in user's role — basic users never see demo barcodes regardless
// of how the flag is set. Use useDemoMode() / useDemoAudience() only in
// the admin-settings UI where the raw stored values must be edited.
//
// Setter side-effect: each toggle fires an audit-event POST so the
// change appears in the Logs page. The toggles don't otherwise touch the
// BE (state is device-local) — the audit call is the only signal.

const STORAGE_KEY = "demo-mode";
const AUDIENCE_KEY = "demo-mode-audience";
const EVENT_NAME = "demo-mode-change";

export type DemoAudience = "all-admins" | "super-admin";
const DEFAULT_AUDIENCE: DemoAudience = "all-admins";

const safeRead = (): boolean => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
        return false;
    }
};

const safeReadAudience = (): DemoAudience => {
    try {
        return window.localStorage.getItem(AUDIENCE_KEY) === "super-admin"
            ? "super-admin"
            : DEFAULT_AUDIENCE;
    } catch {
        return DEFAULT_AUDIENCE;
    }
};

const notify = (detail: unknown): void => {
    try {
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    } catch {
        // CustomEvent unsupported — graceful no-op.
    }
};

export const isDemoMode = (): boolean => safeRead();

export const setDemoMode = (on: boolean): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, on ? "true" : "false");
    } catch {
        // Ignore — storage may be disabled (incognito).
    }
    // Same-tab listeners get notified via a custom event; cross-tab via the
    // browser's built-in 'storage' event.
    notify(on);
    fireAuditEvent({
        department: "Settings",
        action: "developer.demo_mode.set",
        metadata: { value: on },
    });
};

export const setDemoAudience = (value: DemoAudience): void => {
    try {
        window.localStorage.setItem(AUDIENCE_KEY, value);
    } catch {
        // Ignore — storage may be disabled.
    }
    notify(value);
    fireAuditEvent({
        department: "Settings",
        action: "developer.demo_audience.set",
        metadata: { value },
    });
};

const useDemoStorage = <T,>(read: () => T): T => {
    const [v, setV] = useState<T>(() => read());
    useEffect(() => {
        const sync = () => setV(read());
        window.addEventListener("storage", sync);
        window.addEventListener(EVENT_NAME, sync);
        return () => {
            window.removeEventListener("storage", sync);
            window.removeEventListener(EVENT_NAME, sync);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return v;
};

/** Raw on/off flag — for the admin-settings toggle UI. */
export const useDemoMode = (): boolean => useDemoStorage(safeRead);

/** Raw audience setting — for the admin-settings toggle UI. */
export const useDemoAudience = (): DemoAudience => useDemoStorage(safeReadAudience);

/**
 * Whether demo barcodes should render for the current user. Combines the
 * device-level flag, the device-level audience setting, and the logged-in
 * user's role. Basic users never see demo barcodes.
 */
export const useDemoVisible = (): boolean => {
    const flag = useDemoMode();
    const audience = useDemoAudience();
    if (!flag) return false;
    const position = readUserPosition();
    if (isAdminPosition(position)) return true;
    if (isManagerPlus(position)) return audience === "all-admins";
    return false;
};
